import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import TextInput from 'react/elements/TextInput';
import { setMealIdeaIngredients, MEAL_IDEAS_LOADED } from 'redux/actions/mealIdeas';
import useOffline, { onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

// The widths of meal_idea_ingredients' columns (Meal_Idea_Repository), so a long entry is cut short
// here rather than refused by the server.
const FIELDS = [
	{ name: 'quantity', label: 'Amount', placeholder: '2 cups, 1 box, a handful', maxLength: 60 },
	{ name: 'notes', label: 'Notes', placeholder: 'Chopped, ripe, the big one', maxLength: 500 },
];

const fieldsOf = (ingredient) => ({ quantity: ingredient.quantity || '', notes: ingredient.notes || '' });

/**
 * How much of an item one meal idea takes, in the item editor opened from that idea's ingredient:
 * its amount and notes, which go onto the grocery list with it. Each saves when it is left, as the
 * whole ingredient list with this one changed, the way every ingredient edit is sent. Leaving one
 * empty clears it. Online-only, so offline both are read-only, titled "Needs a connection", and one
 * left offline saves once back online.
 */
export default function IngredientInIdea({ idea, ingredient }) {
	const dispatch = useDispatch();
	const [fields, setFields] = useState(() => fieldsOf(ingredient));
	const [error, setError] = useState(null);
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);
	// Leaving a field for the back button saves it, and the answer comes after this is gone.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	useEffect(() => {
		setFields(fieldsOf(ingredient));
	}, [ingredient.id]);

	/**
	 * Both fields go with every save, as they stand: leaving one and then the other before the first
	 * answer is in would otherwise send the second without the first.
	 */
	const saveField = (name) => {
		if(fields[name].trim() === (ingredient[name] || '')) {
			return;
		}
		const changed = { quantity: fields.quantity.trim(), notes: fields.notes.trim() };
		dispatch(setMealIdeaIngredients(idea.id, (list) => list.map((each) =>
			(each.id === ingredient.id ? { ...each, ...changed } : each)))).then((action) => {
			if(mounted.current) {
				setError(action && action.type === MEAL_IDEAS_LOADED ? null : 'That did not save. Try again.');
			}
		});
	};

	return (
		<section className='meal_idea_panel__section grocery_item_details__fields ingredient_in_idea'>
			<h6>In {idea.name}</h6>
			{!!error && <p className='meal_idea_panel__name-error'>{error}</p>}
			{FIELDS.map((field) =>
			<label key={field.name} className='grocery_item_details__field'>
				<span className='grocery_item_details__label'>{field.label}</span>
				<span className='input-field'>
					<TextInput
						aria-label={field.label}
						placeholder={field.placeholder}
						maxLength={field.maxLength}
						{...onlineOnlyField(offline)}
						value={fields[field.name]}
						onChange={(event) => {
							const value = event.target.value;
							setFields((current) => ({ ...current, [field.name]: value }));
						}}
						onBlur={() => whenOnline(field.name, () => saveField(field.name))}
						onKeyDown={(event) => {
							if(event.key === 'Enter') {
								event.preventDefault();
								event.target.blur();
							}
						}}
					/>
				</span>
			</label>
			)}
		</section>
	);
}
